/*
 * Provisioning lead times — single source of truth.
 *
 * Background: the 14 September 2026 audit reconciliation surfaced FOUR
 * mutually contradictory sets of provisioning-time copy across the site:
 *
 *   1. Shared CTA on every service page and location page:
 *      "Typical provisioning takes 5–10 business days"
 *   2. Per-service Tech Specs tables on each /services/* page:
 *      IP Transit 5–10, Ethernet/L2VPN 7–14, DWDM 10–20,
 *      Dark Fibre — Sofia metro 15–30
 *   3. UnifiedConfigurator getProvisionTime():
 *      dark-fibre 30–90, DWDM 5–10, everything else ~48 hours
 *   4. Location page CTA:
 *      "Typical provisioning is 5–10 business days"
 *
 * A customer viewing the DWDM page saw 10–20 days in the table, 5–10 in
 * the CTA below it, and 5–10 again in the configurator on the same journey.
 * Dark fibre ranged from 15–30 to 30–90 depending on where you looked.
 *
 * This file is now the ONLY place these numbers live. Every surface reads
 * from here.
 *
 * VALUES
 * -----------------------------------------------------------------------
 * The values below match set (2) — the per-service Tech Specs tables.
 * Confirmed by the commercial owner on 15 September 2026.
 *
 * To change these, edit the `days` tuples below and every surface follows
 * automatically. No code change is needed.
 */

import type { ServiceId } from "@/data/service-availability";

/**
 * Half-open days range, inclusive on both ends. `[min, max]`.
 * `min === max` renders as a single value ("10 days"); `min < max`
 * renders as a range ("5–10 days").
 */
export type LeadTimeDays = readonly [number, number];

export interface ProvisioningLeadTime {
  /** Half-open inclusive range in business days. */
  readonly days: LeadTimeDays;
  /**
   * Optional footnote explaining a scope caveat. Rendered in prose
   * where the surface has room for it (Tech Specs label suffix, not
   * the CTA). Bilingual because location pages and future translated
   * service pages both consume it.
   */
  readonly scopeNote?: { en: string; bg: string };
}

/**
 * Per-service provisioning lead times.
 *
 * NB: keys use the canonical ServiceId from @/data/service-availability
 * (dark-fibre, ethernet-l2vpn, dwdm, ip-transit). The configurator's
 * legacy ProductId shape (l2vpn, dark-fiber) is mapped in
 * `provisioningForConfiguratorProduct` below.
 */
export const PROVISIONING_LEAD_TIMES: Record<ServiceId, ProvisioningLeadTime> = {
  // Confirmed 15 September 2026.
  // Configurator previously said "~48 hours" — the outlier that prompted the audit.
  "ip-transit": {
    days: [5, 10],
  },
  // Confirmed 15 September 2026.
  // Configurator previously said "~48 hours".
  "ethernet-l2vpn": {
    days: [7, 14],
  },
  // Confirmed 15 September 2026.
  // Configurator previously said "5–10 days" (this range's lower bound).
  "dwdm": {
    days: [10, 20],
  },
  // Confirmed 15 September 2026.
  // Configurator previously said "30–90 days" — a range that only makes
  // sense for a national or cross-border build, not for Sofia metro.
  // The Sofia metro is the ONLY dark fibre delivery footprint today per
  // src/data/service-availability.ts (DARK_FIBRE_POPS).
  "dark-fibre": {
    days: [15, 30],
    scopeNote: {
      en: "Sofia metro",
      bg: "София метро",
    },
  },
};

/* ─── Formatters ─── */

/**
 * "5–10 days" / "10 days" / "30 days" — the compact spec-table value.
 * Kept ASCII short-dash — the site uses en-dash elsewhere but the
 * inherited Tech Specs strings shipped with the plain hyphen and we
 * are matching them rather than changing rendering.
 */
export function formatLeadTimeDays(entry: ProvisioningLeadTime, lang: "en" | "bg" = "en"): string {
  const [min, max] = entry.days;
  const dayWord = lang === "bg" ? "дни" : "days";
  const value = min === max ? String(min) : `${min}\u2013${max}`;
  const base = `${value} ${dayWord}`;
  if (!entry.scopeNote) return base;
  return `${base} \u2014 ${entry.scopeNote[lang]}`;
}

/**
 * "Typical provisioning takes 5–10 business days." — full CTA sentence,
 * bilingual, ending with a period. Matches the tone of the previous
 * hard-coded string exactly so nothing about the page rhythm changes.
 */
export function formatLeadTimeCta(entry: ProvisioningLeadTime, lang: "en" | "bg" = "en"): string {
  const [min, max] = entry.days;
  const value = min === max ? String(min) : `${min}\u2013${max}`;
  if (lang === "bg") {
    return `Типичното въвеждане в експлоатация отнема ${value} работни дни.`;
  }
  return `Typical provisioning takes ${value} business days.`;
}

/* ─── Lookups ─── */

/**
 * Convenience lookup by canonical ServiceId.
 */
export function leadTimeFor(service: ServiceId): ProvisioningLeadTime {
  return PROVISIONING_LEAD_TIMES[service];
}

/**
 * Map the configurator's legacy ProductId shape to the canonical
 * ServiceId, then look up. Returns null if the product id is unknown
 * (defensive — the configurator's union is closed today, but the
 * runtime map keeps the site rendering rather than throwing if a new
 * product slug is added upstream).
 */
export function provisioningForConfiguratorProduct(
  productId: "ip-transit" | "l2vpn" | "dwdm" | "dark-fiber",
): ProvisioningLeadTime | null {
  const map: Record<typeof productId, ServiceId> = {
    "ip-transit": "ip-transit",
    "l2vpn": "ethernet-l2vpn",
    "dwdm": "dwdm",
    "dark-fiber": "dark-fibre",
  };
  const serviceId = map[productId];
  return serviceId ? PROVISIONING_LEAD_TIMES[serviceId] : null;
}
